<template>
  <ValidationObserver
    ref="QuickReplyObserver"
    v-slot="{ valid, pristine }" >
    <b-modal
      id="new-quick-reply-modal"
      ref="quickReplyModalRef"
      size="md"
      centered
      hide-header-close
      @shown="autoFocus"
      @hidden="resetData">
      <!-- Modal Title -->
      <template #modal-title>
        <div>
          {{ replyModalId ? 'Edit' : 'Create new' }} quick reply
        </div>
      </template>

      <div>
        <b-form-group>
          <ValidationProvider
            v-slot="{ errors }"
            ref="quickReplyNameValidatorProvider"
            rules="required"
            name="Name" >
            <label
              for="create-quick-reply-modal-name"
              class="col-12">
              Name
            </label>
            <div class="quick-reply-input-wrapper">
              <b-form-input
                id="create-quick-reply-modal-name"
                ref="replyModalNameRef"
                v-model="replyModalName"
                autocomplete="off"
                autofocus
                class="quick-reply-input sb2-bordered sb2-hoverable sb2-focusable"
                type="text"
                placeholder="Enter keyword..."
                @input="testIfQuickReplyExists" />
            </div>
            <div
              v-if="errors[0]"
              class="error flex flex-align-center">
              <SBIcon
                class="mr-2"
                :icon="SBIconList.ERROR"
                :color="SBColors.redPrimary"
                :size="12" />
              <span>{{ errors[0] }}</span>
            </div>
            <div
              v-if="!errors[0] && quickReplyExists"
              class="error flex flex-align-center">
              <SBIcon
                class="mr-2"
                :icon="SBIconList.ERROR"
                :color="SBColors.redPrimary"
                :size="12" />
              <span>Quick reply already exists.</span>
            </div>
          </ValidationProvider>
        </b-form-group>
        <b-form-group>
          <ValidationProvider
            v-slot="{ errors }"
            ref="messageValidationProviderRef"
            name="Message"
            rules="required">
            <b-form-group>
              <label
                for="message-field"
                class="settings__label" >
                Message
              </label>
              <SBTextInput
                id="message-field"
                ref="messageArea"
                v-model="quickReplyText"
                :focus-shadow="true"
                :show-tags-select="true"
                placeholder="Enter message..." />
              <div
                v-if="errors[0]"
                class="error flex flex-align-center">
                <SBIcon
                  class="mr-2"
                  :icon="SBIconList.ERROR"
                  :color="SBColors.redPrimary"
                  :size="12" />
                <span>{{ errors[0] }}</span>
              </div>
            </b-form-group>
          </ValidationProvider>
        </b-form-group>
      </div>

      <template
        slot="modal-footer">
        <b-button
          :disabled="saving"
          @click="hide">
          Cancel
        </b-button>
        <b-button
          variant="primary"
          :disabled="saving || !valid || pristine || (quickReplyExists !== false)"
          :class="{loading: saving}"
          class="flex settings__actions--save"
          @click="submit">
          <span>Save</span>
          <Spinner
            v-if="saving"
            :size="14"
            :thickness="2"
            :color="SBColors.white" />
        </b-button>
      </template>
    </b-modal>
  </ValidationObserver>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import ToasterMixin from '@/mixins/Toaster';
import SBIcon from '../../../common/Icon';
import SBIconList from '../../../../helpers/lists/sb-icon-list';
import SBColors from '../../../../helpers/lists/colors';
import SBTextInput from '@/components/business/widgets/SBTextInput';
import Spinner from '@/components/common/Spinner';
import { whatsappFormatMessage } from '@/helpers/stringHelpers';
import { swapCustomFieldIdWithCustomFieldName, swapCustomFieldNameWithCustomFieldId } from '@/helpers/quickRepliesParser';

export default {
  name: 'QuickReplyEditorModal',
  components: {
    Spinner,
    SBIcon,
    SBTextInput,
  },
  mixins: [
    ToasterMixin,
  ],
  data() {
    return {
      replyModalId: null,
      replyModalName: '',
      quickReplyText: '',
      initialQuickReplyText: null,
      saving: false,
      quickReplyExists: false,
      SBIconList,
      SBColors,
    };
  },
  computed: {
    ...mapGetters({
      quickReplyList: 'chat/quickReplyList',
    }),
  },
  methods: {
    ...mapActions({
      createQuickReply: 'chat/createQuickReply',
      updateQuickReply: 'chat/updateQuickReply',
    }),
    autoFocus() {
      if (this.replyModalId) {
        this.$refs.messageArea.setText(this.initialQuickReplyText);
        this.$refs.replyModalNameRef.focus();
      } else if (!this.replyModalId && this.replyModalName) {
        this.$refs.messageArea.focusInput();
      }
    },
    resetData() {
      this.saving = false;
      this.replyModalName = '';
      this.quickReplyText = '';
      this.initialQuickReplyText = '';
      this.replyModalId = null;
      this.quickReplyExists = false;
      this.$refs.QuickReplyObserver.reset();
    },
    hide() {
      this.$refs.quickReplyModalRef.hide();
    },
    show(modalData = null) {
      if (modalData) {
        this.replyModalId = modalData.id;
        this.replyModalName = modalData.keyword;
        this.initialQuickReplyText = `${swapCustomFieldIdWithCustomFieldName(modalData.text)} `;
      }
      this.$refs.quickReplyModalRef.show();
    },
    submit(e) {
      e.preventDefault();
      const quickReplyText = whatsappFormatMessage(this.quickReplyText).trim();
      this.saving = true;
      const data = {
        keyword: this.replyModalName.trim(),
        text: swapCustomFieldNameWithCustomFieldId(quickReplyText),
      };
      if (this.replyModalId) {
        this.updateQuickReply({
          replyId: this.replyModalId,
          data,
        })
          .then((response) => {
            if (response.status === 200) {
              this.showToaster('Quick reply has been successfully updated.', null, null, 'OK');
              this.hide();
            } else {
              this.saving = false;
            }
          })
          .catch(() => {
            this.saving = false;
          });
      } else {
        this.createQuickReply(data)
          .then((response) => {
            if (response.status === 200) {
              this.showToaster('Quick reply has been successfully created.', null, null, 'OK');
              this.hide();
            } else {
              this.saving = false;
            }
          })
          .catch(() => {
            this.saving = false;
          })
          .finally(() => {});
      }
    },
    testIfQuickReplyExists() {
      this.quickReplyExists = false;
      this.$refs.quickReplyNameValidatorProvider.validate()
        .then(() => {
          if (this.$refs.quickReplyNameValidatorProvider.flags.valid) {
            const matchingQuickReply = this.quickReplyList.find(qr => qr.keyword.toLowerCase() === this.replyModalName.toLowerCase());
            if (matchingQuickReply) {
              this.quickReplyExists = true;
            }
          }
        });
    },
  },
};
</script>

<style lang="scss" scoped>
#new-quick-reply-modal {
  .quick-reply-input-wrapper {
    position: relative;

    &::before {
      content: '/';
      display: block;
      position: absolute;
      top: 7px;
      left: 10px;
    }

    .quick-reply-input {
      padding-left: 14px !important;
    }
  }

  .settings__actions--save {
    box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.15) !important;
    background-color: $bluePrimary !important;
    color: $white !important;
    position: relative !important;
    border: 1px solid $bluePrimary !important;

    &:hover,
    &:focus,
    &:active {
      background-color: $bluePrimary !important;
      border: 1px solid $bluePrimary !important;
      color: $white !important;
    }

    &.loading span {
      visibility: hidden;
    }

    .loader {
      position: absolute;
      height: 100%;
      display: flex;
      align-items: center;
    }

    &:disabled {
      opacity: 1 !important;

      &:not(.loading) {
        background: $greyLight !important;
        border: 1px solid $greyLight !important;

        &:hover {
          background: $greyLight !important;
          border: 1px solid $greyLight !important;
        }
      }
    }
  }
}
</style>
